/**
 * Pre-Visit Clinical Hand-off PDF Generator
 *
 * An MD-facing clinical document the patient prepares for their physician,
 * structured as a "best-resident hand-off": a clean Reason for Visit, a
 * bottom-line-up-front Clinical Snapshot, a narrative HPI in clinician voice,
 * and grouped Pertinent Positives + explicit Pertinent Negatives.
 *
 * v227.7.0 redesign (pure hand-off):
 *  - Renders the structured `handoff` block on pre_visit_summary
 *    (generate_handoff_summary on the API). NEVER dumps raw transcript or
 *    questionnaire chips, and NEVER prints a diagnosis/differential.
 *  - Page 1 is a true facesheet: identity, insurance, allergies (flagged),
 *    meds, PMH, Reason for Visit, Clinical Snapshot.
 *  - Page 2 is the hand-off body: narrative HPI -> Pertinent Positives ->
 *    Pertinent Negatives (with an explicit "not addressed" line) -> background.
 *  - Page 3 keeps the assessment as Pending (the claiming MD's job) and the
 *    claim CTA (the conversion hook). No differential, red flags, or exam plan.
 *  - Logo (LOGO_WHITE_WORDMARK), QR, and claim URL helpers unchanged.
 *
 * Uses window.open + print (same proven approach as MyMedicalNote.jsx).
 */
import { generateQRDataURL } from './qrcode.jsx'
import { LOGO_WHITE_WORDMARK } from './logoBase64.js'
import { buildClaimUrl, CLAIM_DISPLAY, CLAIM_BASE } from './claimUrl.js'
import { Capacitor } from '@capacitor/core'
import { sharePdfFromHtml } from './sharePdf'
import { formatSex } from './formatSex'
import { formatPlanType } from './formatPlanType'
import { formatDate } from './dateUtils'
// TEST 1.03 — the ONE coercion; see src/utils/profileConditions.js. `hx.conditions?.length`
// does not throw on the object shape, it reads undefined and prints 'Not documented' to a
// physician for a patient who has a documented history. A drop, not a crash, and worse.
import { toConditionList } from './profileConditions'
import {
  normalizeFacesheet,
  facesheetHasIdentity,
  facesheetName,
  isPresent,
  FacesheetUnavailableError,
} from './facesheet'

const NAVY = '#15354F'
const BLUE = '#46A8F9'
const GREEN = '#66D170'
const LIGHT_BLUE = '#A7D9FC'
const ICE = '#E9F8FF'
const BODY = '#1E293B'
const MUTED = '#64748B'
const SLATE = '#94A3B8'

// Escape AI/free-text before injecting into the document
function escapeHtml(s) {
  return String(s == null ? '' : s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
}

// Format allergies from the facesheet.
// ★★★ `isPresent` — NOT truthiness. An absent section is `{}`, which is truthy,
//     and that is how an unloaded profile used to reach the affirmative
//     negatives at the bottom of these formatters.
function formatAllergies(fs) {
  if (!isPresent(fs, 'allergies')) return '<p class="muted">Not documented</p>'
  const allergies = fs.allergies || {}

  const rows = []

  if (allergies.hasMedAllergies === false) {
    rows.push('<div class="allergy"><span class="tag drug">Drug</span><span>NKDA — No Known Drug Allergies</span></div>')
  } else if (allergies.medList?.length > 0) {
    allergies.medList.forEach(a => {
      const sev = a.severity === 'severe' ? '<span class="severity-severe">(SEVERE)</span>' : a.severity ? `<span class="severity-mild">(${a.severity})</span>` : ''
      const reaction = a.reaction ? ` — ${a.reaction}` : ''
      rows.push(`<div class="allergy"><span class="tag drug">Drug</span><span>${a.name || a} ${sev}${reaction}</span></div>`)
    })
  }

  if (allergies.hasFoodAllergies === false) {
    rows.push('<div class="allergy"><span class="tag food">Food</span><span>NKFA — No Known Food Allergies</span></div>')
  } else if (allergies.foodList?.length > 0) {
    allergies.foodList.forEach(a => {
      const sev = a.severity ? `<span class="severity-mild">(${a.severity})</span>` : ''
      rows.push(`<div class="allergy"><span class="tag food">Food</span><span>${a.name || a} ${sev}</span></div>`)
    })
  }

  // Legacy format
  if (rows.length === 0 && allergies.list?.length > 0) {
    allergies.list.forEach(a => {
      const sev = a.severity ? `<span class="severity-mild">(${a.severity})</span>` : ''
      rows.push(`<div class="allergy"><span class="tag drug">Allergy</span><span>${a.name || a} ${sev}</span></div>`)
    })
  }

  if (rows.length === 0 && (allergies.hasAllergies === false || allergies.noKnownAllergies === true)) {
    return '<div class="allergy"><span class="tag drug">Drug</span><span>NKDA — No Known Drug Allergies</span></div>'
  }

  return rows.join('') || '<p class="muted">Not documented</p>'
}

// Format medications from the facesheet.
function formatMedications(fs) {
  if (!isPresent(fs, 'medications')) return '<p class="muted">Not documented</p>'
  const meds = fs.medications || {}
  // ★ "None reported" is a CLAIM and needs the patient's own flag behind it.
  if (meds.hasMedications === false || meds.noMedications === true) return '<p>None reported</p>'
  if (meds.list?.length > 0) {
    return meds.list.map(m => {
      const dose = m.dosage ? ` ${m.dosage}` : ''
      const freq = m.frequency ? `, ${m.frequency}` : ''
      const purpose = m.purpose ? ` <span class="mut">— ${m.purpose}</span>` : ''
      return `<div class="med-row"><strong>${m.name}</strong>${dose}${freq}${purpose}</div>`
    }).join('')
  }
  return '<p class="muted">Not documented</p>'
}

// Format past medical & surgical history from the facesheet.
//
// ★★★ THIS IS THE ONE THAT DID CLINICAL HARM. `hx` was `{}` on an unloaded
//     profile — truthy — so both ternaries fell to their else branch and the
//     document told the receiving physician the patient reported NO conditions
//     and NO surgeries. One real hand-off said "None reported" for a patient
//     with a documented GERD history, on a subacute rhinitis work-up.
//     An empty list is not a denial. Only the patient's own flag is.
function formatMedicalHistory(fs) {
  const NOT = 'Not documented'
  if (!isPresent(fs, 'medicalHistory')) return { conditions: NOT, surgeries: NOT }
  const hx = fs.medicalHistory || {}

  const condList = toConditionList(hx.conditions)
  const conditions = condList.length > 0
    ? condList.map(c => c.year ? `${c.name} <span class="mut">(${c.year})</span>` : c.name).join('<br/>')
    : (hx.noConditions === true ? 'None reported' : NOT)

  const surgeries = hx.surgeries?.length > 0
    ? hx.surgeries.map(s => s.year ? `${s.name} <span class="mut">(${s.year})</span>` : s.name).join('<br/>')
    : (hx.noSurgeries === true ? 'None reported' : NOT)

  return { conditions, surgeries }
}

// Format family history from the facesheet.
function formatFamilyHistory(fs) {
  if (!isPresent(fs, 'familyHistory')) return 'Not documented'
  const fh = fs.familyHistory || {}
  if (fh.noKnownHistory) return 'No significant family history reported'

  const conditionNames = {
    heartDisease: 'Heart Disease', highBloodPressure: 'High Blood Pressure',
    stroke: 'Stroke', diabetes: 'Diabetes', thyroidDisorder: 'Thyroid Disorder',
    asthma: 'Asthma', hearingLoss: 'Hearing Loss', allergiesRhinitis: 'Allergies/Rhinitis',
    eczemaPsoriasis: 'Eczema/Psoriasis', skinCancer: 'Skin Cancer', cancer: 'Cancer',
    migraines: 'Migraines', depressionAnxiety: 'Depression/Anxiety',
    kidneyDisease: 'Kidney Disease', arthritis: 'Arthritis', osteoporosis: 'Osteoporosis',
  }

  const parts = []
  const famList = [...toConditionList(fh.conditions), ...toConditionList(fh.otherConditions)]
  if (famList.length > 0) {
    famList.forEach(c => {
      const relatives = [c.mother && 'Mother', c.father && 'Father', c.sibling && 'Sibling'].filter(Boolean)
      if (relatives.length > 0) {
        const name = conditionNames[c.nameKey] || c.name || c.nameKey || 'Condition'
        parts.push(`<div class="hx-line"><span class="mut">${name}:</span> ${relatives.join(', ')}</div>`)
      }
    })
  }
  // ★ "Non-contributory" is an assertion. It requires the patient to have
  //   actually completed the section — otherwise we have simply not asked.
  if (parts.length > 0) return parts.join('')
  return fh.completed === true ? 'Non-contributory' : 'Not documented'
}

// Format social history from the facesheet.
function formatSocialHistory(fs) {
  if (!isPresent(fs, 'socialLifestyle')) return 'Not documented'
  const s = fs.socialLifestyle || {}
  const parts = []
  if (s.smoking) parts.push(`<div class="hx-line"><span class="mut">Tobacco:</span> ${s.smoking}${s.smokingFrequency ? ` (${s.smokingFrequency})` : ''}</div>`)
  if (s.alcohol) parts.push(`<div class="hx-line"><span class="mut">Alcohol:</span> ${s.alcohol}${s.alcoholFrequency ? ` (${s.alcoholFrequency})` : ''}</div>`)
  if (s.exercise) parts.push(`<div class="hx-line"><span class="mut">Exercise:</span> ${s.exercise}${s.exerciseFrequency ? ` (${s.exerciseFrequency})` : ''}</div>`)
  if (s.occupation) parts.push(`<div class="hx-line"><span class="mut">Occupation:</span> ${s.occupation}</div>`)
  if (parts.length > 0) return parts.join('')
  return s.completed === true ? 'Non-contributory' : 'Not documented'
}

// ★★★ RETIRED IN v242.146.0. This built its own calendar:
//     `new Date('1984-02-11')` is parsed as UTC midnight by specification, and
//     rendering that in America/Los_Angeles lands on the PREVIOUS DAY at 4pm.
//     The hand-off printed `Feb 10, 1984` for a patient born on the 11th — a
//     patient-identification field, one day wrong, on a document a physician
//     uses to match a patient. `utils/dateUtils.js` OWNS THE CALENDAR and
//     already builds a date-only value at local noon for exactly this reason.
//     Do not reintroduce a local date formatter here.
const formatDateShort = formatDate

// ★★★ RETIRED IN v242.145.0. This returned the STRING '?' for a missing DOB and
//     the template printed it unguarded as `(?y)` — a template artefact handed
//     to a physician. The age now comes from the facesheet as a NUMBER OR
//     NOTHING (utils/facesheet.js), so the renderer has nothing to mistake.
//     Do not reintroduce a local age helper here.

/**
 * Generate and open the Pre-Visit Clinical Hand-off PDF
 */
// v242.88.0: `shareText` / `shareTitle` are optional. When present, the native
// path attaches the generated PDF to a share sheet carrying that message — the
// difference between "Save PDF" (file only) and "Share with doctor" (file plus
// the share code the MD needs to claim the case). TF b34 #14.
// ★ TEST 3.14 (patient push 3a): the document is built by buildVisitPrepHtml (exported, so a
//   gate can read the real HTML) and output by generateVisitPrepPDF; the two halves are the old
//   function, cut in two.
export async function generateVisitPrepPDF(args) {
  const { shareText, shareTitle } = args || {}
  const { html, docTitle } = await buildVisitPrepHtml(args)

  if (Capacitor.isNativePlatform()) {
    await sharePdfFromHtml(html, docTitle, {
      ...(shareText ? { text: shareText } : {}),
      ...(shareTitle ? { title: shareTitle, dialogTitle: shareTitle } : {}),
    })
    return
  }
  const printWindow = window.open('', '_blank')
  if (!printWindow) {
    alert('Please allow popups to download your Pre-Visit Clinical Hand-off')
    return
  }
  printWindow.document.write(html)
  printWindow.document.close()
  setTimeout(() => { printWindow.print() }, 800)
}

export async function buildVisitPrepHtml({ currentCase, facesheet, profile, shareCode, qrDataUrl, externalDoctor, preVisitSummary, userPhone, userEmail }) {
  // ★★★ ONE SOURCE, NORMALISED ONCE. `facesheet` is the server's answer; the
  //     local profile store is a FALLBACK and nothing more. Both go through the
  //     same normaliser so this function has exactly one input shape.
  const fs = normalizeFacesheet(facesheet || profile, {
    source: facesheet ? 'server' : 'store',
    userPhone,
    userEmail,
  })

  // ★★★ THE EMISSION RULE (Dr. Rami, 2026-08-27: "that should be impossible").
  //     A hand-off with no patient on it is not a document, and the caller must
  //     deal with that rather than a physician. The old code detected exactly
  //     this condition and used it to pick a FILENAME.
  if (!facesheetHasIdentity(fs)) {
    throw new FacesheetUnavailableError(
      'Refusing to generate a hand-off with no patient identity (name + date of birth)')
  }

  const id = fs.identification || {}
  const patientName = facesheetName(fs)
  const dob = formatDateShort(fs.dob)
  const age = fs.age                 // a NUMBER or null. Never the string '?'.
  const sex = formatSex(fs.sex)      // SEX-CAPITALISE-SWEEP-2
  const phone = fs.phone
  const email = fs.email
  const addressParts = [id.streetAddress, id.city, id.state, id.zipCode].filter(Boolean)
  const addressLine = addressParts.length > 1
    ? (id.streetAddress ? `${id.streetAddress}, ` : '') + [id.city, id.state].filter(Boolean).join(', ') + (id.zipCode ? ` ${id.zipCode}` : '')
    : ''

  // Insurance — digested text only (no card images)
  const insurance = isPresent(fs, 'insurance') ? fs.insurance : null
  let insuranceHTML
  if (insurance?.selfPay) {
    insuranceHTML = '<div class="ins-grid"><div><div class="fl">Coverage</div><div class="fv">Self-Pay</div></div></div>'
  } else if (insurance) {
    const cell = (label, val, muted) => `<div><div class="fl">${label}</div><div class="fv${muted ? ' muted' : ''}">${val || 'Not provided'}</div></div>`
    const cells = [
      cell('Insurer', insurance.insurerName, !insurance.insurerName),
      // ★ v242.146.0 PLANTYPE-CAPITALISE-1 — the column is stored lowercase on
      //   purpose (authRequired.js compares on it), so the capitalisation
      //   happens at render. It printed `ppo` to a physician until now.
      cell('Plan', formatPlanType(insurance.planType), !insurance.planType),
      cell('Member ID', insurance.memberId, !insurance.memberId),
      cell('Group', insurance.groupNumber || insurance.groupId, !(insurance.groupNumber || insurance.groupId)),
    ]
    if (insurance.subscriberName) cells.push(cell('Subscriber', insurance.subscriberName))
    if (insurance.relationship) cells.push(cell('Relationship', insurance.relationship))
    insuranceHTML = `<div class="ins-grid">${cells.join('')}</div>`
  } else {
    insuranceHTML = '<div class="ins-grid"><div><div class="fv muted">Not provided</div></div></div>'
  }

  const specialtyName = currentCase.specialtyName || 'General'
  const focusAreaName = currentCase.focusAreaName || currentCase.focus_area || ''
  const rawCaseNum = currentCase.caseNumber || currentCase.id?.slice(0, 8) || ''
  const caseNumber = String(rawCaseNum).replace(/^#+/, '')
  const createdDate = formatDateShort(currentCase.createdAt || new Date().toISOString())
  // v229.3.0: PDF generation timestamp — this document is a point-in-time snapshot;
  // the online case (via the share code) is always the most up-to-date source.
  const generatedAt = (() => {
    try {
      return new Date().toLocaleString('en-US', {
        month: 'short', day: 'numeric', year: 'numeric',
        hour: 'numeric', minute: '2-digit',
        timeZone: 'America/Los_Angeles', timeZoneName: 'short',
      })
    } catch { return new Date().toLocaleString() }
  })()

  const DOC_LABEL = 'Pre-Visit Clinical Hand-off'
  // ★ The old form was `patientName !== 'Patient' ? ...` — the code KNEW the
  //   name was missing and used it to pick a filename instead of to stop.
  //   Identity is now guaranteed above, so the name is simply always here.
  const docTitle = `${DOC_LABEL} - #${caseNumber} - ${patientName}`

  const doctorName = externalDoctor?.name || ''
  const preparedLine = doctorName ? `Prepared by the patient for ${doctorName}` : 'Prepared by the patient'

  // ── Hand-off block (rides on pre_visit_summary.handoff) ──
  let pvsObj = preVisitSummary || currentCase.preVisitSummary || currentCase.pre_visit_summary || {}
  if (typeof pvsObj === 'string') {
    try { pvsObj = JSON.parse(pvsObj) } catch { pvsObj = {} }
  }
  const handoff = (pvsObj && typeof pvsObj === 'object' && pvsObj.handoff && typeof pvsObj.handoff === 'object') ? pvsObj.handoff : {}
  const hasHandoff = !!(handoff.snapshot || handoff.hpi || (Array.isArray(handoff.pertinentPositives) && handoff.pertinentPositives.length))

  // Reason for visit — clean one-liner; NEVER raw voice transcript
  const reasonForVisit = (handoff.reasonForVisit && handoff.reasonForVisit.trim())
    || (focusAreaName ? `Patient presents for evaluation of ${focusAreaName.toLowerCase()}.` : 'See history below.')

  // Clinical Snapshot (BLUF)
  const snapshotText = (handoff.snapshot && handoff.snapshot.trim()) || ''
  const snapshotHTML = snapshotText ? `
    <div class="sh">Clinical Snapshot${hasHandoff ? '<span class="ai-badge">Orella AI</span>' : ''}</div>
    <div class="snapshot">
      <div class="snap-cap">Bottom line up front</div>
      <div class="snap-text">${escapeHtml(snapshotText)}</div>
    </div>` : ''

  // Narrative HPI
  const hpiText = (handoff.hpi && handoff.hpi.trim()) || ''
  const hpiHTML = hpiText
    ? hpiText.split(/\n\s*\n/).map(p => `<p>${escapeHtml(p.trim()).replace(/\n/g, '<br/>')}</p>`).join('')
    : `<p class="muted">A narrative history could not be generated automatically. The complete patient-reported intake is available in the full case via the share code.</p>`

  // Pertinent positives (grouped)
  const positives = Array.isArray(handoff.pertinentPositives) ? handoff.pertinentPositives : []
  const posHTML = positives.length ? `
    <div class="sh">Pertinent Positives</div>
    <div class="pos-groups">${positives.map(g => `
      <div class="pos-group">
        <div class="pg-title"><span class="pg-dot"></span>${escapeHtml(g.group || 'Findings')}</div>
        <ul class="cl">${(Array.isArray(g.items) ? g.items : []).map(it => `<li>${escapeHtml(it)}</li>`).join('')}</ul>
      </div>`).join('')}</div>` : ''

  // Pertinent negatives (+ explicit not-addressed line)
  const negatives = Array.isArray(handoff.pertinentNegatives) ? handoff.pertinentNegatives : []
  const notAddressed = Array.isArray(handoff.notAddressed) ? handoff.notAddressed : []
  const negHTML = (negatives.length || notAddressed.length) ? `
    <div class="sh">Pertinent Negatives</div>
    <div class="neg-block">
      ${negatives.length ? `<ul class="cl cols">${negatives.map(n => `<li>${escapeHtml(n)}</li>`).join('')}</ul>` : '<p class="muted">No explicit negatives recorded in intake.</p>'}
      ${notAddressed.length ? `<div class="not-addressed"><b>Not addressed in intake:</b> ${notAddressed.map(escapeHtml).join(', ')}. Stated only where the patient was asked — not assumed normal.</div>` : ''}
    </div>` : ''

  // Facesheet chart data — all five from the ONE normalised source.
  const allergiesHTML = formatAllergies(fs)
  const medsHTML = formatMedications(fs)
  const medHx = formatMedicalHistory(fs)
  const familyHxHTML = formatFamilyHistory(fs)
  const socialHxHTML = formatSocialHistory(fs)

  // Attachments (count only — full-resolution files gated behind the share code).
  // v227.9.1: include `documents` (detect stores photos / videos / documents) — the
  // prior count summed only photos+videos, so uploaded files/scans were invisible.
  const asArray = (raw) => {
    if (Array.isArray(raw)) return raw
    if (typeof raw === 'string') { try { const p = JSON.parse(raw); return Array.isArray(p) ? p : [] } catch { return [] } }
    return []
  }
  const photos = asArray(currentCase.detect?.photos || currentCase.photos || [])
  const videos = asArray(currentCase.detect?.videos || currentCase.videos || [])
  const documents = asArray(currentCase.detect?.documents || currentCase.documents || [])
  const photoCount = photos.length + videos.length + documents.length
  // Human-readable breakdown for the markers ("3 photos · 1 document")
  const attachmentParts = []
  if (photos.length) attachmentParts.push(`${photos.length} ${photos.length === 1 ? 'photo' : 'photos'}`)
  if (videos.length) attachmentParts.push(`${videos.length} ${videos.length === 1 ? 'video' : 'videos'}`)
  if (documents.length) attachmentParts.push(`${documents.length} ${documents.length === 1 ? 'document' : 'documents'}`)
  const attachmentBreakdown = attachmentParts.join(' · ')

  // QR + display URL from the single source of truth
  const claimUrl = buildClaimUrl(shareCode)
  // ★★ TEST 3.14 (patient push 3a): the direct link to THIS case lived only inside the QR, and
  //   the entry page was plain text — most physicians open the PDF at a computer, where a QR
  //   is useless and plain text has to be retyped. Page 1 now prints the direct link under the
  //   share code, and every mention of the entry page is a link. Both from claimUrl.js.
  //   scripts/check-handoff-pdf-links.mjs and check-browser-render R7 hold it.
  const directLink = shareCode
    ? `<a class="link" href="${escapeHtml(claimUrl)}">${escapeHtml(claimUrl.replace(/^https?:\/\//, ''))}</a>`
    : ''
  const entryLink = `<a class="entry" href="${escapeHtml(CLAIM_BASE)}">${escapeHtml(CLAIM_DISPLAY)}</a>`
  let qrImg = qrDataUrl
  if (!qrImg && shareCode) {
    qrImg = await generateQRDataURL(claimUrl, { size: 200, color: NAVY })
  }
  const qrSmall = qrImg ? `<img src="${qrImg}" width="54" height="54" style="display:block;border-radius:7px;"/>` : ''
  const qrLarge = qrImg ? `<img src="${qrImg}" width="92" height="92" style="display:block;border-radius:8px;"/>` : `<div style="width:92px;height:92px;border:2px dashed #CBD5E1;border-radius:8px;"></div>`

  const lockIcon = '<svg viewBox="0 0 24 24" fill="none" stroke="' + BLUE + '" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="width:20px;height:20px;"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>'
  const filesIcon = '<svg viewBox="0 0 24 24" fill="none" stroke="' + BLUE + '" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="width:15px;height:15px;"><path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"/></svg>'

  const html = `<!DOCTYPE html>
<html>
<head>
<title>${docTitle}</title>
<style>
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap');
  * { margin:0; padding:0; box-sizing:border-box; -webkit-print-color-adjust:exact; print-color-adjust:exact; }
  html, body { -webkit-print-color-adjust:exact; print-color-adjust:exact; }
  body { font-family:'Inter',system-ui,sans-serif; color:${BODY}; font-size:10px; line-height:1.6; }
  .page { max-width:720px; margin:0 auto; }

  .header { background:${NAVY}; padding:22px 32px; display:flex; justify-content:space-between; align-items:flex-start; }
  .header .wm { height:19px; display:block; }
  .header .sub { font-size:8px; font-weight:700; letter-spacing:3.5px; text-transform:uppercase; color:rgba(255,255,255,0.5); margin-top:9px; }
  .header .prep { font-size:8.5px; color:rgba(255,255,255,0.66); margin-top:6px; }
  .header-right { display:flex; align-items:center; gap:12px; }
  .header-right .qr { background:#fff; border-radius:8px; padding:5px; line-height:0; }
  .header-right .code-lbl { font-size:7px; font-weight:700; letter-spacing:2px; text-transform:uppercase; color:rgba(255,255,255,0.4); text-align:right; }
  .header-right .code { font-family:'SF Mono',ui-monospace,monospace; font-size:14px; font-weight:700; letter-spacing:2px; color:#fff; margin-top:2px; text-align:right; }
  .header-right .link { display:block; font-size:8px; font-weight:600; color:${LIGHT_BLUE}; text-decoration:underline; margin-top:3px; text-align:right; }
  a.entry { color:inherit; text-decoration:underline; }

  .accent { height:2px; background:${BLUE}; }
  .meta { padding:7px 32px; display:flex; justify-content:space-between; font-size:8.5px; color:${SLATE}; }

  .header-c { background:${NAVY}; padding:11px 32px; display:flex; justify-content:space-between; align-items:center; }
  .header-c .l { display:flex; align-items:center; gap:8px; }
  .header-c .wm-c { height:13px; }
  .header-c .l span { color:rgba(255,255,255,0.42); font-size:8px; letter-spacing:1px; }
  .header-c .r { font-family:ui-monospace,monospace; font-size:8.5px; letter-spacing:2px; color:rgba(255,255,255,0.5); }

  .content { padding:22px 32px 24px; }

  .sh { font-size:8.5px; font-weight:700; text-transform:uppercase; letter-spacing:2.5px; color:${NAVY}; border-bottom:1px solid rgba(21,53,79,0.14); padding-bottom:6px; margin:20px 0 10px; }
  .sh:first-child { margin-top:0; }
  .sh.blue { color:${BLUE}; }
  .sh .ai-badge { font-size:6.5px; font-weight:700; background:${ICE}; color:${BLUE}; padding:2px 6px; border-radius:3px; letter-spacing:0.5px; margin-left:7px; }

  .grid-3 { display:grid; grid-template-columns:repeat(3,1fr); gap:11px 18px; }
  .grid-2 { display:grid; grid-template-columns:1fr 1fr; gap:9px 22px; }
  .ins-grid { display:grid; grid-template-columns:repeat(4,1fr); gap:10px 18px; }
  .fl { font-size:7px; text-transform:uppercase; letter-spacing:1.5px; color:${SLATE}; margin-bottom:2px; }
  .fv { font-size:11px; font-weight:600; color:${BODY}; }
  .fv.muted { font-weight:500; color:${SLATE}; }

  .insurance-card { background:#F8FAFC; border:1px solid #EEF2F6; border-radius:10px; padding:14px 16px; }

  .cc { font-size:12px; font-weight:600; line-height:1.55; color:${NAVY}; background:${ICE}; border-left:3px solid ${BLUE}; padding:11px 14px; border-radius:0 8px 8px 0; }

  .files-note { display:flex; align-items:flex-start; gap:9px; background:${ICE}; border:1px solid ${LIGHT_BLUE}; border-radius:10px; padding:10px 13px; font-size:10px; color:#334155; line-height:1.5; }
  .files-note .fi { flex-shrink:0; margin-top:1px; }
  .files-note strong { color:${NAVY}; }
  .files-note .mut { color:${MUTED}; }

  .snapshot { border:1px solid #E6EDF3; border-top:3px solid ${NAVY}; border-radius:0 0 8px 8px; padding:12px 15px 13px; }
  .snap-cap { font-size:7.5px; font-weight:700; text-transform:uppercase; letter-spacing:1.5px; color:${SLATE}; margin-bottom:6px; }
  .snap-text { font-size:10.5px; line-height:1.7; color:#334155; }

  .narrative { font-size:10.5px; line-height:1.75; color:#334155; }
  .narrative p { margin-bottom:9px; } .narrative p:last-child { margin-bottom:0; }
  .narrative strong { color:${NAVY}; }

  .pos-groups { display:grid; grid-template-columns:1fr 1fr; gap:4px 26px; }
  .pos-group { margin-bottom:7px; break-inside:avoid; }
  .pg-title { font-size:9.5px; font-weight:700; color:${NAVY}; margin:5px 0 3px; }
  .pg-dot { display:inline-block; width:6px; height:6px; border-radius:2px; background:${BLUE}; margin-right:7px; vertical-align:middle; }
  ul.cl { margin:0 0 6px 16px; }
  ul.cl li { font-size:10px; line-height:1.5; color:#334155; margin-bottom:2px; }
  ul.cl.cols { columns:2; column-gap:26px; margin-left:16px; }

  .neg-block { background:#F8FAFC; border:1px solid #EEF2F6; border-radius:10px; padding:12px 15px 9px; }
  .not-addressed { font-size:9px; color:${MUTED}; font-style:italic; margin-top:8px; padding-top:8px; border-top:1px dashed #E2E8F0; }
  .not-addressed b { color:${SLATE}; font-style:normal; }

  .allergy { display:flex; align-items:center; gap:9px; margin-bottom:5px; font-size:10.5px; }
  .tag { font-size:7px; font-weight:700; padding:2px 7px; border-radius:3px; text-transform:uppercase; letter-spacing:1px; }
  .tag.drug { background:#FEF2F2; color:#DC2626; } .tag.food { background:#FFF7ED; color:#D97706; }
  .severity-severe { color:#DC2626; font-weight:600; font-size:9px; }
  .severity-mild { color:${SLATE}; font-size:9px; }
  .med-row { font-size:10.5px; margin-bottom:4px; } .med-row strong { color:${NAVY}; } .med-row .mut { color:${MUTED}; }
  .hx-line { font-size:10.5px; line-height:1.6; } .hx-line .mut { color:${MUTED}; } .muted { color:${MUTED}; }

  .pending { background:#FFFBEB; border:1px solid #FDE68A; border-radius:8px; padding:11px 14px; }
  .pending .title { font-size:10px; font-weight:700; color:#92400E; }
  .pending .text { font-size:9px; color:#A16207; line-height:1.5; margin-top:2px; }

  .locked { background:#F8FAFC; border:1px dashed #CBD5E1; border-radius:10px; padding:14px 16px; display:flex; align-items:center; gap:14px; }
  .locked .lk { width:40px; height:40px; border-radius:10px; background:${ICE}; display:flex; align-items:center; justify-content:center; flex-shrink:0; }
  .locked .txt .t { font-size:10.5px; font-weight:700; color:${NAVY}; }
  .locked .txt .s { font-size:9px; color:${MUTED}; margin-top:2px; line-height:1.5; }

  .trust { display:flex; gap:8px; margin-top:14px; }
  .tb { font-size:7px; font-weight:700; padding:3px 8px; border-radius:4px; }
  .tb.h { background:#F0FDF4; color:#16A34A; border:1px solid #BBF7D0; }
  .tb.e { background:${ICE}; color:${BLUE}; border:1px solid ${LIGHT_BLUE}; }
  .tb.o { background:#F8FAFC; color:#64748B; border:1px solid #E2E8F0; }
  .disc { font-size:7.5px; color:${SLATE}; line-height:1.55; margin-top:11px; }

  .invite { margin:20px 0 4px; border:1.5px solid ${NAVY}; border-radius:14px; overflow:hidden; }
  .invite .ih { background:${NAVY}; padding:18px 22px; text-align:center; }
  .invite .ih .wm { height:16px; margin-bottom:9px; }
  .invite .ih h3 { color:#fff; font-size:12px; font-weight:700; }
  .invite .ih p { color:rgba(255,255,255,0.62); font-size:9px; line-height:1.55; margin-top:5px; max-width:420px; margin-left:auto; margin-right:auto; }
  .invite .ib { background:#F8FAFC; padding:18px 22px; display:flex; gap:18px; align-items:center; }
  .invite .ib .qr { background:#fff; border-radius:10px; padding:7px; border:1px solid #E2E8F0; line-height:0; }
  .invite .ib .info .cl { font-size:8px; font-weight:700; text-transform:uppercase; letter-spacing:1.5px; color:${SLATE}; }
  .invite .ib .info .code { font-family:ui-monospace,monospace; font-size:20px; font-weight:800; letter-spacing:3px; color:${NAVY}; margin:2px 0 9px; }
  .invite .ib .info .b { display:flex; align-items:center; gap:6px; font-size:9px; color:${MUTED}; margin-bottom:4px; }
  .invite .ib .info .b .ck { color:${GREEN}; font-weight:900; }
  .invite .if { background:${BLUE}; padding:9px 22px; text-align:center; }
  .invite .if .u { color:#fff; font-size:11px; font-weight:700; }
  .invite .if .s { color:rgba(255,255,255,0.78); font-size:8px; margin-top:1px; }
  .invite .if .g { color:rgba(255,255,255,0.92); font-size:8px; margin-top:5px; padding-top:5px; border-top:1px solid rgba(255,255,255,0.22); }
  .invite .if .g strong { font-weight:700; }

  .pf { margin-top:18px; padding-top:8px; border-top:1px solid #F1F5F9; display:flex; justify-content:space-between; font-size:7px; color:#CBD5E1; }
  .center-foot { text-align:center; font-size:7px; color:#CBD5E1; margin-top:8px; }

  @media print {
    * { -webkit-print-color-adjust:exact !important; print-color-adjust:exact !important; }
    .page { max-width:100%; }
  }
  /* v227.9.1: continuous flow — cohesive blocks never split across printed sheets,
     so the document fills each page top-to-bottom instead of stranding short tails
     behind forced page breaks (which produced large dead space in v227.7–227.9.0). */
  .snapshot, .neg-block, .pending, .locked, .invite, .pos-group,
  .grid-3, .grid-2, .ins-grid, .insurance-card, .cc, .sh {
    break-inside:avoid; page-break-inside:avoid;
  }
  .sh { break-after:avoid; page-break-after:avoid; }
</style>
</head>
<body>

<!-- ========== PAGE 1: Facesheet + Bottom Line ========== -->
<div class="page">
  <div class="header">
    <div>
      <img class="wm" src="${LOGO_WHITE_WORDMARK}" />
      <div class="sub">${DOC_LABEL}</div>
      <div class="prep">${preparedLine}</div>
    </div>
    <div class="header-right">
      <div class="qr">${qrSmall}</div>
      <div><div class="code-lbl">Share Code</div><div class="code">${shareCode || ''}</div>${directLink}</div>
    </div>
  </div>
  <div class="accent"></div>
  <div class="meta">
    <span>${createdDate}</span>
    <span>${specialtyName}${focusAreaName ? ` · ${focusAreaName}` : ''}</span>
    <span>Case #${caseNumber} · ${patientName}</span>
  </div>

  <div class="content">
    <div class="sh">Patient</div>
    <div class="grid-3">
      <div><div class="fl">Name</div><div class="fv">${patientName}</div></div>
      <div><div class="fl">Date of Birth</div><div class="fv">${dob}${typeof age === 'number' ? ` (${age}y)` : ''}</div></div>
      <div><div class="fl">Sex</div><div class="fv">${sex || 'Not provided'}</div></div>
      <div><div class="fl">Mobile</div><div class="fv">${phone || 'Not provided'}</div></div>
      <div><div class="fl">Email</div><div class="fv">${email || 'Not provided'}</div></div>
      <div><div class="fl">Case #</div><div class="fv">${caseNumber}</div></div>
    </div>
    ${addressLine ? `<div style="margin-top:11px;"><div class="fl">Address</div><div class="fv muted">${addressLine}</div></div>` : ''}

    <div class="sh">Insurance on File</div>
    <div class="insurance-card">${insuranceHTML}</div>

    <div class="sh">Allergies</div>
    ${allergiesHTML}

    <div class="sh">Current Medications</div>
    ${medsHTML}

    <div class="sh">Past Medical &amp; Surgical History</div>
    <div class="grid-2">
      <div><div class="fl" style="margin-bottom:5px;">Conditions</div><div class="hx-line">${medHx.conditions}</div></div>
      <div><div class="fl" style="margin-bottom:5px;">Surgical</div><div class="hx-line">${medHx.surgeries}</div></div>
    </div>

    ${photoCount > 0 ? `
    <div class="sh">Patient-Submitted Files</div>
    <div class="files-note">
      <span class="fi">${filesIcon}</span>
      <span><strong>${photoCount} ${photoCount === 1 ? 'file' : 'files'} attached</strong>${attachmentBreakdown ? ` <span class="mut">(${attachmentBreakdown})</span>` : ''} — full-resolution photos, scans, and documents are available to view in the Orella panel using the share code.</span>
    </div>` : ''}

    <div class="sh">Reason for Visit</div>
    <div class="cc">${escapeHtml(reasonForVisit)}</div>

    ${snapshotHTML}

    <div class="sh blue">History of Present Illness${hasHandoff ? '<span class="ai-badge">Orella AI</span>' : ''}</div>
    <div class="narrative">${hpiHTML}</div>

    ${posHTML}
    ${negHTML}

    <div class="sh">Relevant Background</div>
    <div class="grid-2">
      <div><div class="fl" style="margin-bottom:5px;">Family History</div>${familyHxHTML}</div>
      <div><div class="fl" style="margin-bottom:5px;">Social History</div>${socialHxHTML}</div>
    </div>

    <div class="sh">Pre-Visit Assessment</div>
    <div class="pending">
      <div class="title">Pending — to be completed by the physician</div>
      <div class="text">Assessment, diagnoses, treatment plan, and follow-up recommendations populate after the physician evaluates the patient on Orella. This document organizes the patient's own account; it does not contain a diagnosis or clinical opinion.</div>
    </div>

    ${photoCount > 0 ? `
    <div class="sh">Attached Evidence</div>
    <div class="locked">
      <div class="lk">${lockIcon}</div>
      <div class="txt">
        <div class="t">${photoCount} patient-submitted ${photoCount === 1 ? 'file' : 'files'}${attachmentBreakdown ? ` · ${attachmentBreakdown}` : ''} · full resolution locked</div>
        <div class="s">Scan the code or sign in free at ${entryLink} to view high-resolution photos, the complete questionnaire, and to message the patient.</div>
      </div>
    </div>
    ` : ''}

    <div class="trust">
      <span class="tb h">HIPAA Compliant</span>
      <span class="tb e">Encrypted</span>
      <span class="tb o">Patient-Owned Data</span>
    </div>

    <div class="disc">This document contains self-reported patient information organized by Orella Health, a HIPAA-compliant healthcare communication platform. It does not constitute medical advice, diagnosis, or treatment. All clinical decisions are the responsibility of the treating physician.</div>

    <div class="invite">
      <div class="ih">
        <img class="wm" src="${LOGO_WHITE_WORDMARK}" />
        <h3>Open the full case your patient prepared for you</h3>
        <p>Scan the code or enter the share code to claim this case — high-resolution photos, the complete interactive history, and secure messaging with your patient.</p>
      </div>
      <div class="ib">
        <div class="qr">${qrLarge}</div>
        <div class="info">
          <div class="cl">Share Code</div>
          <div class="code">${shareCode || ''}</div>
          <div class="b"><span class="ck">✓</span> Full-resolution patient photos</div>
          <div class="b"><span class="ck">✓</span> Complete questionnaire responses</div>
          <div class="b"><span class="ck">✓</span> Secure HIPAA-compliant platform</div>
          <div class="b"><span class="ck">✓</span> Document &amp; prescribe in one place</div>
        </div>
      </div>
      <div class="if">
        <div class="u">${entryLink}</div>
        <div class="s">Free for physicians · No download required · HIPAA compliant</div>
        <div class="g"><strong>Generated ${generatedAt}.</strong> This PDF is a point-in-time snapshot — scan the code or visit ${entryLink} for the most up-to-date case information.</div>
      </div>
    </div>

    <div class="pf"><span>${DOC_LABEL} · ${patientName}</span><span>${createdDate}</span></div>
    <div class="center-foot">orellahealth.com · admin@orellahealth.com · © 2026 Orella Health Inc.</div>
  </div>
</div>

</body>
</html>`

  return { html, docTitle }
}
